import useReveal from "../lib/useReveal";
import { ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "individual",
    eyebrow: "01 — Individual",
    title: "Individual Health Plans",
    body: "Built from your bloodwork, lifestyle and goals. Customised diet, training and recovery protocols for weight loss, PCOS, diabetes reversal, gut & hormone balance.",
    bullets: [
      "Personalised nutrition + cooking guidance",
      "Weekly measurable strength & cardio goals",
      "Ongoing review with doctor & nutritionist",
    ],
    image:
      "https://images.unsplash.com/photo-1540420773420-3366772f4999?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwb3JnYW5pYyUyMGZvb2QlMjBudXRyaXRpb24lMjBpbmdyZWRpZW50c3xlbnwwfHx8fDE3Nzg3ODc5NzR8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7 lg:row-span-2",
    accent: "bg-[#1E3A2B] text-[#FCFBF7]",
  },
  {
    id: "corporate",
    eyebrow: "02 — Corporate",
    title: "Corporate Wellness Tie-ups",
    body: "Healthier, sharper teams. On-site screenings, lunch-and-learns, group workouts and 1:1 nutrition for your workforce.",
    bullets: [
      "Quarterly health audits",
      "Office yoga & movement programs",
      "Customised cafeteria advisory",
    ],
    image:
      "https://images.unsplash.com/photo-1758874384683-0accd9fb26ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB3ZWxsbmVzcyUyMHlvZ2ElMjBvZmZpY2V8ZW58MHx8fHwxNzc4Nzg3OTc0fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
    accent: "bg-[#C25E3E] text-[#FCFBF7]",
  },
  {
    id: "education",
    eyebrow: "03 — Education",
    title: "School & College Tie-ups",
    body: "Foundations for life — nutrition literacy, fitness modules, sports performance and mental-physical balance for students.",
    bullets: [
      "Age-tailored curriculum",
      "Parent & faculty workshops",
      "Athlete performance tracking",
    ],
    image:
      "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzY2hvb2wlMjBzdHVkZW50cyUyMHBsYXlpbmclMjBzcG9ydHMlMjBoZWFsdGh5fGVufDB8fHx8MTc3ODc4Nzk3NHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
    accent: "bg-[#839788] text-[#1E3A2B]",
  },
];

export const Services = () => {
  const ref = useReveal();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-16 sm:py-24 lg:py-36 bg-[#EAE6DA]/40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 sm:gap-8 mb-10 sm:mb-16">
          <div>
            <span className="uppercase text-[10px] sm:text-xs tracking-[0.28em] text-[#C25E3E] font-semibold">
              Services
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 sm:mt-6 leading-[1.05] text-[#1E3A2B] max-w-2xl">
              Programs that flex around your life —{" "}
              <span className="italic">never the other way.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm sm:text-base text-[#1E3A2B]/70">
            Three formats. One philosophy: customised diet plans + curated
            workouts with goals you can actually measure.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5 sm:gap-6" ref={ref}>
          {services.map((s) => (
            <article
              key={s.id}
              data-testid={`service-${s.id}-card`}
              className={`reveal relative overflow-hidden rounded-3xl bg-[#FCFBF7] border border-[#D5CEBA] p-6 sm:p-8 lg:p-10 ${s.span} hover:-translate-y-1 transition-transform duration-500`}
            >
              <div className="flex justify-between items-start gap-4 mb-6 sm:mb-8">
                <span className="uppercase text-[10px] sm:text-xs tracking-[0.24em] text-[#C25E3E]">
                  {s.eyebrow}
                </span>
                <span
                  className={`inline-flex w-9 h-9 sm:w-10 sm:h-10 items-center justify-center rounded-full ${s.accent}`}
                >
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>

              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#1E3A2B] leading-tight max-w-md">
                {s.title}
              </h3>
              <p className="mt-4 sm:mt-5 text-sm sm:text-base text-[#1E3A2B]/75 max-w-lg leading-relaxed">
                {s.body}
              </p>

              <ul className="mt-5 sm:mt-6 space-y-2">
                {s.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-xs sm:text-sm text-[#1E3A2B]/80"
                  >
                    <span className="mt-1.5 sm:mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#C25E3E] flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-6 sm:mt-8 rounded-2xl overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-48 sm:h-56 lg:h-64 object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
